<template>
  <div v-if="$page" id="page-products">
    <header ref="header" class="header">
      <figure
        v-if="refsReady"
        v-scroll:parallax="{ refNode: $refs.header, ratio: 0.15 }"
        class="header__background"
      >
        <Image v-if="$page.headerImg" :source="$page.headerImg" />
      </figure>

      <div class="header__content">
        <h1 v-scroll:reveal="{ delay: 550 }" class="header__legend">{{ $page.title }}</h1>
        <h2 v-scroll:reveal="{ delay: 600 }" class="header__title">{{ $page.headerTitleTop }}</h2>
        <h3 v-scroll:reveal="{ delay: 650 }" class="header__title">
          {{ $page.headerTitleBottom }}
        </h3>
      </div>
    </header>

    <section :id="$t('navigation.anchors.products.overview')" class="section -indented">
      <h2 class="section__legend -products">{{ $page.introLegend }}</h2>

      <div class="section__content">
        <h3 class="section__title">{{ $page.introTitle }}</h3>
        <div class="row">
          <p class="row__item -span-5">{{ $page.introTextTop }}</p>

          <div class="row__item -skip-1 stat">
            <h4 v-scroll:reveal="{ delay: 650, offset: 0 }" class="stat__value">
              {{ $page.introStat }}
            </h4>
            <p>{{ $page.introStatLabel }}</p>
          </div>
        </div>
      </div>
    </section>

    <section
      :id="$t('navigation.anchors.products.intro')"
      class="section -flat -pad-down-mobile -products"
    >
      <div class="row -spread -mobile -overflow">
        <article
          v-for="(product, idx) in $store.getters.five_products"
          :key="`product-${product.id}`"
          v-scroll:reveal="{ delay: idx * 75 }"
          class="mini-card -overflow"
          :style="{ backgroundImage: `url('${product.image.original}')` }"
        >
          <span class="mini-card__label">{{ product.name }}</span>

          <LangRouterLink
            :to="{ name: 'product.index', params: { slug: product.slug } }"
            class="arrow-link mini-card__link"
          >
            <Icon id="arrow" />
            {{ $t('see_more') }}
          </LangRouterLink>
        </article>
      </div>

      <div class="section__fgroup">
        <p v-scroll:reveal="{ delay: 100 }">{{ $page.introTextBottom }}</p>
        <LangRouterLink
          v-scroll:reveal="{ delay: 125 }"
          :to="{ name: 'contact', hash: '#map' }"
          class="button -border -link"
          >{{ $page.introLinkLabel }}
        </LangRouterLink>
      </div>
    </section>

    <ContentSlider
      v-if="product"
      :id="$t('product.anchor')"
      :legend="$page.productsLegend"
      :name="product.name"
      :links-heading="$page.productLinksHeading"
      :current-index="productIndex"
      :slides-count="$store.getters.products.length"
      :image="product.image"
      :links="product.business_units"
      :text="product.description"
      @index-change="handleProductIndexChange"
      class="--tablet"
    />

    <section class="section --phone mobile-content">
      <MobileContent
        v-for="(product, key) in $store.getters.products"
        :id="key"
        :key="key"
        v-bind="product"
        :legend="getLegend(key)"
        :links="product.business_units"
        :image="product.image"
        :text="product.description"
        :links-heading="$page.productLinksHeading"
      />
    </section>

    <section
      :id="$t('navigation.anchors.products.quality')"
      ref="quality"
      class="section -products"
    >
      <div class="banner -pull-mobile">
        <figure
          v-scroll:parallax="{ ratio: -0.1 }"
          class="banner__image --tablet"
          :style="{ backgroundImage: `url('${$page.qualityImg.original}')` }"
        />

        <div class="banner__legend -align-left" />
      </div>

      <div
        v-if="refsReady"
        v-scroll:parallax="{ refNode: $refs.quality, ratio: 0.0625 }"
        class="section__content -h-pad"
      >
        <h2 v-scroll:reveal class="section__legend">{{ $page.qualityLegend }}</h2>
        <h3 v-scroll:reveal="{ delay: 75 }" class="section__title">{{ $page.qualityTitle }}</h3>

        <div class="row -tablet -gap">
          <RawHtml v-if="$page.qualityText" :html="$page.qualityText" class="row__item" />

          <div class="row__item -span-5-11">
            <TextSlider :slides="$page.qualitySlides" />
          </div>
        </div>
      </div>
    </section>

    <section class="section">
      <div v-scroll:reveal class="cta-card">
        <Image
          v-if="$page.ctaPrioritiesImg"
          class="cta-card__image"
          :source="$page.ctaPrioritiesImg"
        />

        <div class="cta-card__content">
          <RawHtml
            tag="h3"
            class="cta-card__title"
            v-if="$page.ctaPrioritiesTitle"
            :html="{ before: formatString($page.ctaPrioritiesTitle) }"
          />
          <p>{{ $page.ctaPrioritiesText }}</p>
          <LangRouterLink
            :to="{ name: 'sustainability', hash: '#priorities' }"
            class="button -border -alt -link cta-card__link"
            >{{ $page.ctaPrioritiesLinkLabel }}
          </LangRouterLink>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import ContentSlider from '@/components/ContentSlider.vue'
import Icon from '@/components/global/Icon.vue'
import Image from '@/components/global/Image.vue'
import LangRouterLink from '@/components/global/LangRouterLink.vue'
import RawHtml from '@/components/global/RawHtml.vue'
import MobileContent from '@/components/MobileContent.vue'
import TextSlider from '@/components/TextSlider.vue'
import { formatString } from '@/helpers/conversionHelpers.js'
import PageMetaMixin from '@/mixins/PageMetaMixin.js'

export default {
  name: 'Products',
  beetPage: 'products',
  mixins: [PageMetaMixin],
  setup: () => ({ formatString }),
  components: { Image, Icon, TextSlider, ContentSlider, MobileContent, LangRouterLink, RawHtml },
  props: {
    slug: { type: String, default: null },
  },
  data() {
    return {
      refsReady: false,
    }
  },
  computed: {
    productIndex() {
      if (!this.slug) return 0

      return this.$store.getters.products.findIndex((product) => product.slug === this.slug) ?? 0
    },
    product() {
      return (
        this.$store.getters.products.find((product) => product.slug === this.slug) ??
        this.$store.getters.five_products[0]
      )
    },
  },
  mounted() {
    this.$store.dispatch('loadProducts')
    this.$nextTick(() => {
      this.refsReady = true
    })
  },
  methods: {
    handleProductIndexChange(index) {
      const product = this.$store.getters.products[index]
      if (!product) return
      this.$router.push({
        name: 'product.index',
        params: { slug: product.slug },
        hash: `#${this.$t('product.anchor')}`,
      })
    },
    getLegend(key) {
      return key === 0 ? this.$page.productsLegend : null
    },
  },
}
</script>
